import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Button } from '../../../../../../components/Common/UI/Button';
import Icons from '../../../../../../components/Common/Icons/Icons';
import ModalSave from '../../../../Common/Modals/Save';
import { useMessageManagerProvider } from '../../../../../context/messageManager';
import { ApplicationTypeEnum } from '../Step1';
import { dateFormat } from '../../../../../../contstant';

interface BuilderStepsNavigationProps {
  current: number;
  total: number;
  handleCurrentStep: (step: number) => void;
}

function BuilderStepsNavigation({ current, total, handleCurrentStep }: BuilderStepsNavigationProps) {
  const navigate = useNavigate();
  const { id } = useParams();
  const { handleIsTextOnly, state, handleDispatch, selectedModule, handleSave } = useMessageManagerProvider();

  const campaignSaveDialogData = [
    { key: 'ID Number:', value: id || '—' },
    { key: 'Start Date:', value: dayjs(state.step4.startDate).format(dateFormat) },
    { key: 'Description:', value: state.step1.companyName || '—' },
    { key: 'End Date:', value: dayjs(state.step4.endDate).format(dateFormat) },
    {
      key: 'Application:',
      value: state.step1.type ? ApplicationTypeEnum[state.step1.type] : '—',
    },
  ];

  const handleChange = (payload: number) => {
    const newData = state.step2.statement.data.map(el2 =>
      el2.id === selectedModule?.item?.id ? selectedModule.item : el2,
    );

    handleIsTextOnly(false);
    handleDispatch({
      step2: {
        ...state.step2,
        statement: {
          ...state.step2.statement,
          data: newData,
        },
      },
    });
    handleCurrentStep(payload);
  };

  const handleSaveAndExit = () => {
    handleSave();
    navigate('/infoTrac/message-manager', { replace: true });
  };

  return (
    <div className="bg-white p-[10px_20px] rounded-lg shadow-md flex justify-between w-full items-center">
      <div className="text-primary font-bold text-xl">{state.step1.companyName || 'New Campaign'}</div>
      <div className="flex items-center gap-[20px]">
        <div className="font-medium text-primary [&_span]:opacity-60 [&_span]:px-2 [&_span]:font-normal">
          <span>Step</span>
          {current}
          <span>of</span>
          {total}
        </div>
        <Button variant="secondary" disabled={current === 1} onClick={() => handleChange(current - 1)}>
          <ChevronLeftIcon />
          Previous
        </Button>
        {current < total ? (
          <Button variant="secondary" disabled={current === total} onClick={() => handleChange(current + 1)}>
            Next
            <ChevronRightIcon />
          </Button>
        ) : null}
        {current < total ? (
          <Button onClick={handleSaveAndExit}>
            <Icons.SaveFilled className="opacity-60 h-[20px]" />
            Save & Exit
          </Button>
        ) : (
          <ModalSave
            title="Campaign Saved"
            okText="Go to list"
            contentStyle="max-w-[600px]"
            triggerStyle="bg-addition5 !text-white hover:bg-[#009DA8] h-[44px] px-[30px] py-[9px]"
            trigger={
              <div className="flex gap-[5px] items-center font-medium">
                <Icons.SaveFilled className="opacity-60 h-[20px]" />
                Save as Draft
              </div>
            }
            onOpen={() => handleSave()}
            onOk={() => navigate('/infoTrac/message-manager', { replace: true })}
            content={
              <>
                <div className="grid grid-cols-2 gap-x-3">
                  {campaignSaveDialogData.map(({ key, value }) => (
                    <div className="grid grid-cols-[90px_1fr] gap-[10px]" key={key}>
                      <div className="font-medium opacity-80">{key}</div>
                      <div className="opacity-60">{value}</div>
                    </div>
                  ))}
                </div>
                <div className="opacity-80">
                  Your Campaign has been successfully saved. To make it effective, you still need to approve the
                  Campaign.
                </div>
              </>
            }
          />
        )}
      </div>
    </div>
  );
}

export default BuilderStepsNavigation;
